import type { AccountInfo } from '@azure/msal-browser';
import { Configuration, PopupRequest, PublicClientApplication } from '@azure/msal-browser';
import { BASE_PATH } from '@/utils/basePath';

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AAD_CLIENT_ID ?? '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AAD_TENANT_ID ?? 'common'}`,
    // El fallback lleva el prefijo del ambiente: `origin` es sólo esquema+host, así que en el
    // ambiente de desarrollo (servido bajo /dev) Entra devolvía el código a la RAÍZ del dominio
    // —el portal de QA— y el usuario acababa autenticado en el ambiente equivocado.
    // 🔴 Es `BASE_PATH` y no `BASE_URL` a propósito: `BASE_PATH` es cadena VACÍA en QA y PROD, así
    // que el valor sigue siendo `https://host` EXACTAMENTE como antes, sin barra final. Con
    // `BASE_URL` salía `https://host/`, y esa barra de más no coincide con la URI registrada en
    // Entra (lo detectó la prueba de LogoutButton). En dev da `https://host/dev`, que es justo la
    // Redirect URI de tipo SPA que se registró.
    // El pipeline puede seguir imponiendo VITE_REDIRECT_URI, que tiene preferencia.
    redirectUri:
      import.meta.env.VITE_REDIRECT_URI ?? `${window.location.origin}${BASE_PATH}`,
  },
  cache: {
    // localStorage sobrevive mejor el ciclo de redirect que sessionStorage.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

// Instancia MSAL ÚNICA de toda la app (AuthProvider + client.ts la comparten).
// Crear una nueva por llamada rompe: no ve la sesión cacheada -> token vacío -> 401.
export const msalInstance = new PublicClientApplication(msalConfig);

const API_SCOPE = import.meta.env.VITE_API_SCOPE ?? 'api://pmp-pulse/.default';

export const apiScopes: string[] = [API_SCOPE];

// Incluye el scope de la API en el login → el consent se otorga de una vez, así
// acquireTokenSilent(apiScopes) en la primera llamada NO requiere interacción
// (si no, getToken devolvería '' y la primera llamada al API daría 401).
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read', API_SCOPE],
};

/**
 * Espera —acotado— a que MSAL tenga una cuenta activa antes de pedir el access token.
 *
 * `AuthProvider` fija la cuenta tras `initialize()` + `handleRedirectPromise()` + `ssoSilent()`,
 * pero la primera tanda de queries del landing puede dispararse ANTES de que ese efecto termine:
 * `acquireTokenSilent` sin cuenta → token vacío → 401. Medido en PROD el 2026-09-08: el 100% de
 * los 401 de SurveyService eran `GET /v1/view-settings/recognition-rules` por esta carrera, y
 * desaparecían al reintentar unos ms después.
 *
 * Devuelve la cuenta, o `null` si no aparece dentro de `timeoutMs` (no hay sesión: `AuthProvider`
 * ya habrá redirigido a /login, o es Teams/Frontline y esos flujos ni llegan aquí). Sale en cuanto
 * la cuenta existe — el coste normal es una o dos iteraciones de 50 ms.
 *
 * Con `VITE_AUTH_ENABLED !== 'true'` (dev local, modo demo) NUNCA va a haber cuenta MSAL, así que
 * no se espera nada — devuelve de inmediato. Sin esta guarda, cada llamada al API en local se
 * colgaría el timeout completo antes de mandar el token vacío de siempre.
 */
export async function waitForMsalAccount(timeoutMs = 3000): Promise<AccountInfo | null> {
  const current = () => msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  const found = current();
  if (found) return found;

  if (import.meta.env.VITE_AUTH_ENABLED !== 'true') return null;

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    const account = current();
    if (account) return account;
  }
  return current();
}
